import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, Users, Briefcase, MessageSquare, Settings, LogOut, 
  Plus, Edit2, Trash2, Save, X, BarChart3, BookOpen, Star, Award,
  Image, FileText, Share2, Layout, ExternalLink, Zap, Target, 
  CheckCircle, TrendingUp, Video, Quote, Sparkles, Palette, Building, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [dynamicPages, setDynamicPages] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedPage, setSelectedPage] = useState('about');
  const [selectedLandingPage, setSelectedLandingPage] = useState(null);
  
  // Settings state
  const [users, setUsers] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAllData = async () => {
    try {
      const [companyRes, statsRes, servicesRes, coursesRes, certificatesRes, heroSlidesRes, dynamicPagesRes, landingPagesRes, testimonialsRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/company-info`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/admin/services`, getAuthHeaders()),
        axios.get(`${API}/admin/courses`, getAuthHeaders()),
        axios.get(`${API}/admin/certificates`, getAuthHeaders()),
        axios.get(`${API}/admin/hero-slides`, getAuthHeaders()),
        axios.get(`${API}/admin/dynamic-pages`, getAuthHeaders()),
        axios.get(`${API}/landing-pages`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/admin/contact-enquiries`, getAuthHeaders()),
      ]);

      setCompanyInfo(companyRes.data);
      setStats(statsRes.data);
      setServices(servicesRes.data);
      setCourses(coursesRes.data);
      setCertificates(certificatesRes.data);
      setHeroSlides(heroSlidesRes.data);
      setDynamicPages(dynamicPagesRes.data);
      setLandingPages(landingPagesRes.data);
      setTestimonials(testimonialsRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const fetchPageContent = async (pageId) => {
    try {
      const res = await axios.get(`${API}/admin/page-content/${pageId}`, getAuthHeaders());
      setPageContent(prev => ({ ...prev, [pageId]: res.data }));
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleUpdateCompanyInfo = async () => {
    try {
      await axios.put(`${API}/admin/company-info`, editData, getAuthHeaders());
      toast.success('Company info updated successfully');
      setIsEditing(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating company info:', error);
      toast.error('Failed to update company info');
    }
  };

  const handleUpdateStats = async () => {
    try {
      await axios.put(`${API}/admin/stats`, { items: editData.items }, getAuthHeaders());
      toast.success('Stats updated successfully');
      setIsEditing(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating stats:', error);
      toast.error('Failed to update stats');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, getAuthHeaders());
      toast.success('Testimonial deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    
    try {
      await axios.delete(`${API}/admin/contact-enquiries/${id}`, getAuthHeaders());
      toast.success('Enquiry deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast.error('Failed to delete enquiry');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await axios.delete(`${API}/admin/services/${id}`, getAuthHeaders());
      toast.success('Service deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API}/admin/courses/${id}`, getAuthHeaders());
      toast.success('Course deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleDeleteCertificate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    
    try {
      await axios.delete(`${API}/admin/certificates/${id}`, getAuthHeaders());
      toast.success('Certificate deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate');
    }
  };

  const handleDeleteHeroSlide = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    
    try {
      await axios.delete(`${API}/admin/hero-slides/${id}`, getAuthHeaders());
      toast.success('Slide deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error('Failed to delete slide');
    }
  };

  const handleDeleteDynamicPage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      await axios.delete(`${API}/admin/dynamic-pages/${id}`, getAuthHeaders());
      toast.success('Page deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleSavePageContent = async (pageId, content) => {
    try {
      await axios.put(`${API}/admin/page-content/${pageId}`, content, getAuthHeaders());
      toast.success('Page content updated successfully');
      fetchPageContent(pageId);
    } catch (error) {
      console.error('Error saving page content:', error);
      toast.error('Failed to save page content');
    }
  };

  // Settings handlers
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, getAuthHeaders());
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUsername || !newUserPassword) {
      toast.error('Username and password are required');
      return;
    }
    
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users`, {
        username: newUsername,
        password: newUserPassword
      }, getAuthHeaders());
      
      toast.success(`User '${newUsername}' created successfully`);
      setNewUsername('');
      setNewUserPassword('');
      fetchAllData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user '${username}'?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${username}`, getAuthHeaders());
      toast.success(`User '${username}' deleted successfully`);
      fetchAllData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Landing Pages handlers
  const handleSaveLandingPage = async () => {
    try {
      if (selectedLandingPage?.id) {
        await axios.put(`${API}/admin/landing-pages/${selectedLandingPage.id}`, selectedLandingPage, getAuthHeaders());
        toast.success('Landing page updated successfully');
      }
      setSelectedLandingPage(null);
      fetchAllData();
    } catch (error) {
      console.error('Error saving landing page:', error);
      toast.error('Failed to save landing page');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Home size={20} /> },
    { id: 'page-builder', label: 'Page Builder', icon: <Zap size={20} />, isLink: true, href: '/admin/page-builder' },
    { id: 'landing-pages', label: 'Landing Pages', icon: <Target size={20} /> },
    { id: 'hero-slides', label: 'Hero Slides', icon: <Image size={20} /> },
    { id: 'custom-pages', label: 'Custom Pages', icon: <Layout size={20} /> },
    { id: 'pages', label: 'Page Content', icon: <FileText size={20} /> },
    { id: 'company', label: 'Company Info', icon: <Building size={20} /> },
    { id: 'social', label: 'Social Links', icon: <Share2 size={20} /> },
    { id: 'stats', label: 'Statistics', icon: <BarChart3 size={20} /> },
    { id: 'courses', label: 'Courses', icon: <BookOpen size={20} /> },
    { id: 'certificates', label: 'Certificates', icon: <Award size={20} /> },
    { id: 'services', label: 'Services', icon: <Briefcase size={20} /> },
    { id: 'testimonials', label: 'Testimonials', icon: <Users size={20} /> },
    { id: 'enquiries', label: 'Enquiries', icon: <MessageSquare size={20} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={20} /> },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Admin Dashboard
            </h1>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  tab.isLink ? (
                    <a
                      key={tab.id}
                      href={tab.href}
                      data-testid={`admin-tab-${tab.id}`}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors text-slate-600 hover:bg-slate-100 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200"
                    >
                      {tab.icon}
                      <span className="font-medium">{tab.label}</span>
                      <span className="ml-auto text-xs bg-purple-600 text-white px-2 py-0.5 rounded">NEW</span>
                    </a>
                  ) : (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`admin-tab-${tab.id}`}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {tab.icon}
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  )
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Dashboard Overview
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                      <div className="text-4xl font-bold mb-2">{enquiries.length}</div>
                      <div className="text-blue-100">Total Enquiries</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                      <div className="text-4xl font-bold mb-2">{testimonials.length}</div>
                      <div className="text-green-100">Testimonials</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                      <div className="text-4xl font-bold mb-2">{services.length}</div>
                      <div className="text-purple-100">Services Listed</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                      <div className="text-4xl font-bold mb-2">{courses.length}</div>
                      <div className="text-orange-100">Courses</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Landing Pages Tab */}
            {activeTab === 'landing-pages' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Landing Pages
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Manage promotional landing pages like SAP SuccessFactors Training</p>
                  </div>
                  <div className="flex gap-2">
                    {landingPages.length === 0 && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/seed-sap-landing-page`, {}, getAuthHeaders());
                            toast.success('SAP landing page created successfully!');
                            fetchAllData();
                          } catch (error) {
                            console.error('Error seeding landing page:', error);
                            toast.error(error.response?.data?.message || 'Failed to create landing page');
                          }
                        }}
                        data-testid="seed-sap-btn"
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Plus size={18} />
                        <span>Create Main SAP Page</span>
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/admin/seed-all-sap-modules`, {}, getAuthHeaders());
                          toast.success(response.data.message);
                          fetchAllData();
                        } catch (error) {
                          console.error('Error seeding modules:', error);
                          toast.error('Failed to create module pages');
                        }
                      }}
                      data-testid="seed-all-modules-btn"
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                    >
                      <Plus size={18} />
                      <span>Create All SAP Modules</span>
                    </button>
                  </div>
                </div>

                {/* Landing Page Editor */}
                {selectedLandingPage ? (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center border-b border-slate-200 pb-4">
                      <h3 className="text-xl font-semibold">Editing: {selectedLandingPage.slug}</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveLandingPage}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                        >
                          <Save size={18} />
                          <span>Save Changes</span>
                        </button>
                        <button
                          onClick={() => setSelectedLandingPage(null)}
                          className="flex items-center space-x-2 px-4 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                        >
                          <X size={18} />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>


                    {/* SEO Section */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><FileText size={18} /> SEO Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Meta Title</label>
                          <input
                            type="text"
                            value={selectedLandingPage.meta_title || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, meta_title: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Meta Description</label>
                          <input
                            type="text"
                            value={selectedLandingPage.meta_description || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, meta_description: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Hero Section */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><Image size={18} /> Hero Section</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Badge Text</label>
                          <input
                            type="text"
                            value={selectedLandingPage.hero_badge || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, hero_badge: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                            placeholder="e.g., Admissions Open"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Hero Title</label>
                          <input
                            type="text"
                            value={selectedLandingPage.hero_title || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, hero_title: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Hero Subtitle</label>
                          <input
                            type="text"
                            value={selectedLandingPage.hero_subtitle || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, hero_subtitle: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Hero Image URL</label>
                          <input
                            type="text"
                            value={selectedLandingPage.hero_image || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, hero_image: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Hero Description</label>
                          <textarea
                            value={selectedLandingPage.hero_description || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, hero_description: e.target.value})}
                            rows="3"
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Batch Info */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><CheckCircle size={18} /> Batch Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Batch Start Date</label>
                          <input
                            type="text"
                            value={selectedLandingPage.batch_start_date || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, batch_start_date: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                            placeholder="e.g., February 15, 2026"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                          <input
                            type="text"
                            value={selectedLandingPage.batch_duration || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, batch_duration: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                            placeholder="e.g., 8 Weeks"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">WhatsApp Group Link</label>
                          <input
                            type="text"
                            value={selectedLandingPage.whatsapp_link || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, whatsapp_link: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                            placeholder="https://chat.whatsapp.com/..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Stats Section */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 size={18} /> Statistics</h4>
                      <div className="space-y-3">
                        {(selectedLandingPage.stats || []).map((stat, index) => (
                          <div key={index} className="flex gap-3 items-center">
                            <input
                              type="text"
                              value={stat.value}
                              onChange={(e) => {
                                const newStats = [...selectedLandingPage.stats];
                                newStats[index].value = e.target.value;
                                setSelectedLandingPage({...selectedLandingPage, stats: newStats});
                              }}
                              className="w-24 px-3 py-2 border border-slate-300 rounded-sm"
                              placeholder="Value"
                            />
                            <input
                              type="text"
                              value={stat.label}
                              onChange={(e) => {
                                const newStats = [...selectedLandingPage.stats];
                                newStats[index].label = e.target.value;
                                setSelectedLandingPage({...selectedLandingPage, stats: newStats});
                              }}
                              className="flex-1 px-3 py-2 border border-slate-300 rounded-sm"
                              placeholder="Label"
                            />
                            <button
                              onClick={() => {
                                const newStats = selectedLandingPage.stats.filter((_, i) => i !== index);
                                setSelectedLandingPage({...selectedLandingPage, stats: newStats});
                              }}
                              className="text-red-600 hover:text-red-700 p-2"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const newStats = [...(selectedLandingPage.stats || []), {value: '', label: ''}];
                            setSelectedLandingPage({...selectedLandingPage, stats: newStats});
                          }}
                          className="text-blue-600 text-sm flex items-center gap-1"
                        >
                          <Plus size={16} /> Add Stat
                        </button>
                      </div>
                    </div>

                    {/* CTA Section */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><Target size={18} /> Call to Action</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">CTA Title</label>
                          <input
                            type="text"
                            value={selectedLandingPage.cta_title || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, cta_title: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">CTA Button Text</label>
                          <input
                            type="text"
                            value={selectedLandingPage.cta_button_text || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, cta_button_text: e.target.value})}
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">CTA Description</label>
                          <textarea
                            value={selectedLandingPage.cta_description || ''}
                            onChange={(e) => setSelectedLandingPage({...selectedLandingPage, cta_description: e.target.value})}
                            rows="2"
                            className="w-full px-3 py-2 border border-slate-300 rounded-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Preview Link */}
                    <div className="flex justify-end pt-4 border-t border-slate-200">
                      <a 
                        href={`/${selectedLandingPage.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink size={18} />
                        Preview Page
                      </a>
                    </div>
                  </div>
                ) : (
                  /* Landing Pages List */
                  <div className="space-y-4">
                    {landingPages.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Target size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No landing pages found</p>
                      </div>
                    ) : (
                      landingPages.map((page) => (
                        <div key={page.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-lg">{page.hero_title} {page.hero_subtitle}</h3>
                              <p className="text-sm text-slate-500">/{page.slug}</p>
                              <p className="text-sm text-slate-600 mt-1">{page.meta_description}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setSelectedLandingPage({...page})}
                                data-testid={`edit-landing-${page.slug}`}
                                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 text-sm"
                              >
                                <Edit2 size={16} />
                                Edit
                              </button>
                              <a
                                href={`/${page.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700 text-sm"
                              >
                                <ExternalLink size={16} />
                                View
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Company Info Tab */}
            {activeTab === 'company' && companyInfo && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Company Information
                  </h2>
                  {!isEditing ? (
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditData({ ...companyInfo });
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                    >
                      <Edit2 size={18} />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateCompanyInfo}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex items-center space-x-2 px-4 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      value={isEditing ? editData.company_name : companyInfo.company_name}
                      onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
                    <textarea
                      value={isEditing ? editData.address : companyInfo.address}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      disabled={!isEditing}
                      rows="3"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Logo URL</label>
                    <input
                      type="text"
                      value={isEditing ? editData.logo_url : companyInfo.logo_url}
                      onChange={(e) => setEditData({ ...editData, logo_url: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Numbers (comma-separated)</label>
                    <input
                      type="text"
                      value={isEditing ? editData.phone?.join(', ') : companyInfo.phone?.join(', ')}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value.split(',').map(p => p.trim()) })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Addresses (comma-separated)</label>
                    <input
                      type="text"
                      value={isEditing ? editData.email?.join(', ') : companyInfo.email?.join(', ')}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value.split(',').map(e => e.trim()) })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Hero Slides Tab */}
            {activeTab === 'hero-slides' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Homepage Hero Slides
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        title: '',
                        subtitle: '',
                        description: '',
                        button_text: 'Learn More',
                        button_link: '/courses',
                        image_url: '',
                        bg_color: '#1e3a8a',
                        text_color: '#ffffff',
                        overlay_opacity: 70,
                        active: true,
                        order: heroSlides.length
                      });
                    }}
                    data-testid="add-slide-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Add Slide</span>
                  </button>
                </div>

                {/* Add/Edit Slide Form */}
                {isEditing && editData?.hasOwnProperty('button_text') && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editData.id ? 'Edit Slide' : 'Add New Slide'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                        <input
                          type="text"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Transform Your Career"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Subtitle *</label>
                        <input
                          type="text"
                          value={editData.subtitle}
                          onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., With Expert IT Training"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows="2"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Brief description for the slide"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Button Text</label>
                        <input
                          type="text"
                          value={editData.button_text}
                          onChange={(e) => setEditData({ ...editData, button_text: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Explore Courses"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Button Link</label>
                        <input
                          type="text"
                          value={editData.button_link}
                          onChange={(e) => setEditData({ ...editData, button_link: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., /courses, /contact"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Background Image URL</label>
                        <input
                          type="text"
                          value={editData.image_url || ''}
                          onChange={(e) => setEditData({ ...editData, image_url: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://images.unsplash.com/photo-... (optional)"
                        />
                        <p className="text-sm text-slate-500 mt-1">💡 Leave empty to use background color instead</p>
                        {editData.image_url && (
                          <div className="mt-3 border border-slate-200 rounded p-2 bg-slate-50">
                            <p className="text-xs font-medium text-slate-600 mb-2">Preview:</p>
                            <img 
                              src={editData.image_url} 
                              alt="Hero slide preview" 
                              className="w-full h-48 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errorMsg = e.target.parentNode.querySelector('.error-msg');
                                if (!errorMsg) {
                                  const div = document.createElement('div');
                                  div.className = 'error-msg text-xs text-red-500 text-center py-2';
                                  div.textContent = '⚠️ Image failed to load. Please check the URL.';
                                  e.target.parentNode.appendChild(div);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Hero Customization */}
                      <div className="md:col-span-2 bg-blue-50 p-4 rounded border border-blue-200">
                        <h3 className="text-sm font-bold text-slate-800 mb-3">🎨 Hero Customization</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Background Color</label>
                            <input
                              type="color"
                              value={editData.bg_color || '#1e3a8a'}
                              onChange={(e) => setEditData({ ...editData, bg_color: e.target.value })}
                              className="w-full h-10 rounded border border-slate-300"
                            />
                            <p className="text-xs text-slate-500 mt-1">Used if no image</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Text Color</label>
                            <input
                              type="color"
                              value={editData.text_color || '#ffffff'}
                              onChange={(e) => setEditData({ ...editData, text_color: e.target.value })}
                              className="w-full h-10 rounded border border-slate-300"
                            />
                            <p className="text-xs text-slate-500 mt-1">Title & subtitle</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Overlay Darkness (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={editData.overlay_opacity || 70}
                              onChange={(e) => setEditData({ ...editData, overlay_opacity: parseInt(e.target.value) })}
                              className="w-full"
                            />
                            <p className="text-xs text-slate-500 mt-1">{editData.overlay_opacity || 70}% darkness</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.active}
                            onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Active</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={async () => {
                          try {
                            if (editData.id) {
                              await axios.put(`${API}/admin/hero-slides/${editData.id}`, editData, getAuthHeaders());
                              toast.success('Slide updated successfully');
                            } else {
                              await axios.post(`${API}/admin/hero-slides`, editData, getAuthHeaders());
                              toast.success('Slide created successfully');
                            }
                            setIsEditing(false);
                            setEditData(null);
                            fetchAllData();
                          } catch (error) {
                            console.error('Error saving slide:', error);
                            toast.error('Failed to save slide');
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} className="inline mr-2" />
                        Save Slide
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(null);
                        }}
                        className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} className="inline mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Slides List */}
                <div className="space-y-4">
                  {heroSlides.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Image size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No hero slides yet. Click "Add Slide" to create one.</p>
                    </div>
                  ) : (
                    heroSlides.map((slide, index) => (
                      <div key={slide.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-4">
                            {slide.image_url && (
                              <img src={slide.image_url} alt={slide.title} className="w-32 h-20 object-cover rounded" />
                            )}
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Slide {index + 1}</span>
                                {!slide.active && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Inactive</span>
                                )}
                              </div>
                              <h3 className="font-bold text-lg text-slate-900">{slide.title}</h3>
                              <p className="text-slate-600 text-sm">{slide.subtitle}</p>
                              <p className="text-slate-500 text-xs mt-1">Button: {slide.button_text} → {slide.button_link}</p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                setEditData({ ...slide });
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteHeroSlide(slide.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-sm"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Custom Pages Tab */}
            {activeTab === 'custom-pages' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Custom Pages
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Create new pages like Gallery, FAQ, Careers, etc.</p>
                  </div>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        title: '',
                        slug: '',
                        meta_description: '',
                        meta_keywords: '',
                        hero_title: '',
                        hero_subtitle: '',
                        hero_image_url: '',
                        hero_bg_color: '#0f172a',
                        hero_text_color: '#ffffff',
                        hero_overlay_opacity: 70,
                        sections: [],
                        show_in_nav: true,
                        nav_order: 100,
                        active: true
                      });
                    }}
                    data-testid="add-page-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Create New Page</span>
                  </button>
                </div>

                {/* Add/Edit Page Form */}
                {isEditing && editData?.hasOwnProperty('slug') && editData?.hasOwnProperty('nav_order') && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editData.id ? 'Edit Page' : 'Create New Page'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Page Title *</label>
                        <input
                          type="text"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Gallery, FAQ, Careers"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">URL Slug * <span className="text-xs text-slate-500">(lowercase, no spaces)</span></label>
                        <input
                          type="text"
                          value={editData.slug}
                          onChange={(e) => setEditData({ ...editData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., gallery, faq, careers"
                        />
                        <p className="text-xs text-slate-500 mt-1">URL: /page/{editData.slug || 'your-slug'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Title *</label>
                        <input
                          type="text"
                          value={editData.hero_title}
                          onChange={(e) => setEditData({ ...editData, hero_title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Main heading for the page"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Subtitle</label>
                        <input
                          type="text"
                          value={editData.hero_subtitle || ''}
                          onChange={(e) => setEditData({ ...editData, hero_subtitle: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Subheading for the page"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Background Image URL</label>
                        <input
                          type="text"
                          value={editData.hero_image_url || ''}
                          onChange={(e) => setEditData({ ...editData, hero_image_url: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://... (leave empty for gradient background)"
                        />
                        {editData.hero_image_url && (
                          <div className="mt-2 border border-slate-200 rounded p-2 bg-slate-50">
                            <p className="text-xs font-medium text-slate-600 mb-2">Preview:</p>
                            <img 
                              src={editData.hero_image_url} 
                              alt="Hero Preview" 
                              className="w-full h-40 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errorMsg = e.target.parentNode.querySelector('.error-msg');
                                if (!errorMsg) {
                                  const div = document.createElement('div');
                                  div.className = 'error-msg text-xs text-red-500 text-center py-2';
                                  div.textContent = '⚠️ Image failed to load. Please check URL.';
                                  e.target.parentNode.appendChild(div);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Hero Colors */}
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Background Color</label>
                        <input
                          type="color"
                          value={editData.hero_bg_color || '#1e3a8a'}
                          onChange={(e) => setEditData({ ...editData, hero_bg_color: e.target.value })}
                          className="w-full h-12 rounded border border-slate-300"
                        />
                        <p className="text-xs text-slate-500 mt-1">Used if no background image is provided</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Text Color</label>
                        <input
                          type="color"
                          value={editData.hero_text_color || '#ffffff'}
                          onChange={(e) => setEditData({ ...editData, hero_text_color: e.target.value })}
                          className="w-full h-12 rounded border border-slate-300"
                        />
                        <p className="text-xs text-slate-500 mt-1">Color for hero title and subtitle</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Overlay Darkness</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={editData.hero_overlay_opacity || 70}
                          onChange={(e) => setEditData({ ...editData, hero_overlay_opacity: parseInt(e.target.value) })}
                          className="w-full"
                        />
                        <p className="text-xs text-slate-500 mt-1">Overlay: {editData.hero_overlay_opacity || 70}% (Only applies if background image is set)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">SEO Description</label>
                        <textarea
                          value={editData.meta_description || ''}
                          onChange={(e) => setEditData({ ...editData, meta_description: e.target.value })}
                          rows="2"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Description for search engines"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">SEO Keywords</label>
                        <input
                          type="text"
                          value={editData.meta_keywords || ''}
                          onChange={(e) => setEditData({ ...editData, meta_keywords: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="keyword1, keyword2, keyword3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Nav Order</label>
                        <input
                          type="number"
                          value={editData.nav_order}
                          onChange={(e) => setEditData({ ...editData, nav_order: parseInt(e.target.value) || 100 })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="100"
                        />
                        <p className="text-xs text-slate-500 mt-1">Lower number = appears earlier in nav</p>
                      </div>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.show_in_nav}
                            onChange={(e) => setEditData({ ...editData, show_in_nav: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Show in Navigation</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.active}
                            onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Active</span>
                        </label>
                      </div>

                      {/* Content Sections Editor */}
                      <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-4">
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-slate-700 mb-3">Page Content Sections</label>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { type: 'text', title: '', content: '', bg_color: '#ffffff', text_color: '#000000' };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 flex items-center justify-center gap-1"
                            >
                              <FileText size={16} /> Text
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { type: 'image', image_url: '', caption: '', bg_color: '#ffffff' };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200 flex items-center justify-center gap-1"
                            >
                              <Image size={16} /> Image
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'link_button', 
                                  title: '', 
                                  link_text: 'Click Here', 
                                  link_url: 'https://',
                                  link_style: 'button',
                                  bg_color: '#ffffff',
                                  button_color: '#3b82f6'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-indigo-100 text-indigo-700 text-sm rounded hover:bg-indigo-200 flex items-center justify-center gap-1"
                            >
                              <ExternalLink size={16} /> Link/Button
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'cta', 
                                  title: '', 
                                  description: '', 
                                  button_text: 'Contact Us', 
                                  button_link: '/contact',
                                  bg_color: '#3b82f6',
                                  text_color: '#ffffff',
                                  button_style: 'primary'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-purple-100 text-purple-700 text-sm rounded hover:bg-purple-200 flex items-center justify-center gap-1"
                            >
                              <Zap size={16} /> CTA
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { type: 'faq', title: 'FAQ', items: [{ question: '', answer: '' }], bg_color: '#ffffff' };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-yellow-100 text-yellow-700 text-sm rounded hover:bg-yellow-200 flex items-center justify-center gap-1"
                            >
                              <MessageSquare size={16} /> FAQ
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'features', 
                                  title: 'Our Features', 
                                  items: [
                                    { icon: '🚀', title: 'Feature 1', description: 'Description here' },
                                    { icon: '⚡', title: 'Feature 2', description: 'Description here' },
                                    { icon: '🎯', title: 'Feature 3', description: 'Description here' }
                                  ],
                                  bg_color: '#f8fafc',
                                  columns: 3
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-cyan-100 text-cyan-700 text-sm rounded hover:bg-cyan-200 flex items-center justify-center gap-1"
                            >
                              <Sparkles size={16} /> Features
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'stats', 
                                  title: 'Our Impact', 
                                  items: [
                                    { number: '500+', label: 'Students Trained' },
                                    { number: '98%', label: 'Placement Rate' },
                                    { number: '50+', label: 'Companies' }
                                  ],
                                  bg_color: '#1e40af',
                                  text_color: '#ffffff'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-indigo-100 text-indigo-700 text-sm rounded hover:bg-indigo-200 flex items-center justify-center gap-1"
                            >
                              <TrendingUp size={16} /> Stats
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'video', 
                                  title: '', 
                                  video_url: '', 
                                  description: '',
                                  bg_color: '#000000'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 flex items-center justify-center gap-1"
                            >
                              <Video size={16} /> Video
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'testimonial', 
                                  name: '', 
                                  role: '', 
                                  company: '',
                                  message: '',
                                  image_url: '',
                                  rating: 5,
                                  bg_color: '#fef3c7'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-orange-100 text-orange-700 text-sm rounded hover:bg-orange-200 flex items-center justify-center gap-1"
                            >
                              <Quote size={16} /> Testimonial
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newSection = { 
                                  type: 'contact_form',
                                  title: 'Get In Touch',
                                  subtitle: 'Fill out the form below and we will get back to you',
                                  show_name: true,
                                  show_email: true,
                                  show_phone: true,
                                  show_service: true,
                                  show_message: true,
                                  button_text: 'Send Message',
                                  bg_color: '#ffffff',
                                  button_color: '#3b82f6'
                                };
                                setEditData({ ...editData, sections: [...(editData.sections || []), newSection] });
                              }}
                              className="px-3 py-2 bg-teal-100 text-teal-700 text-sm rounded hover:bg-teal-200 flex items-center justify-center gap-1"
                            >
                              <MessageSquare size={16} /> Contact Form
                            </button>
                          </div>
                        </div>

                        {/* Sections List */}
                        <div className="space-y-4">
                          {(editData.sections || []).map((section, sectionIndex) => (
                            <div key={sectionIndex} className="border border-slate-200 rounded-lg p-4 bg-white">
                              <div className="flex justify-between items-center mb-3">
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded uppercase font-semibold">
                                  {section.type} Section
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newSections = editData.sections.filter((_, i) => i !== sectionIndex);
                                    setEditData({ ...editData, sections: newSections });
                                  }}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>

                              {/* Text Section */}
                              {section.type === 'text' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Section Title (optional)"
                                  />
                                  <textarea
                                    value={section.content || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, content: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    rows="4"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Enter your text content here..."
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Background Color</label>
                                      <input
                                        type="color"
                                        value={section.bg_color || '#ffffff'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Text Color</label>
                                      <input
                                        type="color"
                                        value={section.text_color || '#000000'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, text_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded border border-slate-200">
                                    <label className="text-xs font-semibold text-slate-700 block mb-2">Text Styling Options</label>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <label className="text-xs text-slate-600 block mb-1">Font Size</label>
                                        <select
                                          value={section.font_size || 'base'}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, font_size: e.target.value };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                                        >
                                          <option value="sm">Small</option>
                                          <option value="base">Normal</option>
                                          <option value="lg">Large</option>
                                          <option value="xl">X-Large</option>
                                          <option value="2xl">2X-Large</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-xs text-slate-600 block mb-1">Font Weight</label>
                                        <select
                                          value={section.font_weight || 'normal'}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, font_weight: e.target.value };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                                        >
                                          <option value="normal">Normal</option>
                                          <option value="medium">Medium</option>
                                          <option value="semibold">Semi Bold</option>
                                          <option value="bold">Bold</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-xs text-slate-600 block mb-1">Text Align</label>
                                        <select
                                          value={section.text_align || 'left'}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, text_align: e.target.value };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                                        >
                                          <option value="left">Left</option>
                                          <option value="center">Center</option>
                                          <option value="right">Right</option>
                                          <option value="justify">Justify</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="mt-2 flex gap-2">
                                      <label className="flex items-center text-xs">
                                        <input
                                          type="checkbox"
                                          checked={section.italic || false}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, italic: e.target.checked };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="mr-1"
                                        />
                                        <span className="italic">Italic</span>
                                      </label>
                                      <label className="flex items-center text-xs">
                                        <input
                                          type="checkbox"
                                          checked={section.underline || false}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, underline: e.target.checked };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="mr-1"
                                        />
                                        <span className="underline">Underline</span>
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Image Section */}
                              {section.type === 'image' && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                      Image URL <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={section.image_url || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, image_url: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="https://images.unsplash.com/photo-xxx or https://i.imgur.com/xxx.jpg"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                      💡 Tip: Use Unsplash, Imgur, or ImgBB for image hosting
                                    </p>
                                  </div>
                                  {section.image_url && (
                                    <div className="border border-slate-200 rounded p-2 bg-slate-50">
                                      <p className="text-xs font-medium text-slate-600 mb-2">Preview:</p>
                                      <img 
                                        src={section.image_url} 
                                        alt="Preview" 
                                        className="max-w-full h-auto max-h-48 rounded mx-auto"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const errorMsg = e.target.parentNode.querySelector('.error-msg');
                                          if (!errorMsg) {
                                            const div = document.createElement('div');
                                            div.className = 'error-msg text-xs text-red-500 text-center py-2';
                                            div.textContent = '⚠️ Image failed to load. Please check the URL.';
                                            e.target.parentNode.appendChild(div);
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    value={section.caption || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, caption: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Image caption (optional)"
                                  />
                                </div>
                              )}

                              {/* Link/Button Section */}
                              {section.type === 'link_button' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Section Title (optional)"
                                  />
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Link Text *</label>
                                      <input
                                        type="text"
                                        value={section.link_text || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, link_text: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="Click Here"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">URL *</label>
                                      <input
                                        type="text"
                                        value={section.link_url || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, link_url: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="https://example.com or /page/about"
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-3">
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Style</label>
                                      <select
                                        value={section.link_style || 'button'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, link_style: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-2 py-2 border border-slate-300 rounded text-sm"
                                      >
                                        <option value="button">Button</option>
                                        <option value="link">Text Link</option>
                                        <option value="outline">Outline Button</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Background</label>
                                      <input
                                        type="color"
                                        value={section.button_color || '#3b82f6'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, button_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Open in</label>
                                      <select
                                        value={section.open_new_tab ? 'new' : 'same'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, open_new_tab: e.target.value === 'new' };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-2 py-2 border border-slate-300 rounded text-sm"
                                      >
                                        <option value="same">Same Tab</option>
                                        <option value="new">New Tab</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded border">
                                    <p className="text-xs font-medium text-slate-600 mb-2">Preview:</p>
                                    {section.link_style === 'button' && (
                                      <button 
                                        className="px-6 py-2 text-white rounded-lg font-medium"
                                        style={{ backgroundColor: section.button_color || '#3b82f6' }}
                                      >
                                        {section.link_text || 'Click Here'}
                                      </button>
                                    )}
                                    {section.link_style === 'link' && (
                                      <span className="text-blue-600 underline hover:text-blue-800 cursor-pointer">
                                        {section.link_text || 'Click Here'}
                                      </span>
                                    )}
                                    {section.link_style === 'outline' && (
                                      <button 
                                        className="px-6 py-2 rounded-lg font-medium border-2"
                                        style={{ borderColor: section.button_color || '#3b82f6', color: section.button_color || '#3b82f6' }}
                                      >
                                        {section.link_text || 'Click Here'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* CTA Section */}
                              {section.type === 'cta' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-3">
                                    <input
                                      type="text"
                                      value={section.title || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, title: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="CTA Title"
                                    />
                                    <input
                                      type="text"
                                      value={section.description || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, description: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="CTA Description"
                                    />
                                    <input
                                      type="text"
                                      value={section.button_text || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, button_text: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Button Text"
                                    />
                                    <input
                                      type="text"
                                      value={section.button_link || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, button_link: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Button Link (/contact)"
                                    />
                                  </div>
                                  <div className="grid grid-cols-3 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Background Color</label>
                                      <input
                                        type="color"
                                        value={section.bg_color || '#3b82f6'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Text Color</label>
                                      <input
                                        type="color"
                                        value={section.text_color || '#ffffff'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, text_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Button Style</label>
                                      <select
                                        value={section.button_style || 'primary'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, button_style: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      >
                                        <option value="primary">Solid</option>
                                        <option value="outline">Outline</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded border border-slate-200">
                                    <label className="text-xs font-semibold text-slate-700 block mb-2">Button Animation</label>
                                    <select
                                      value={section.button_animation || 'none'}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, button_animation: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    >
                                      <option value="none">No Animation</option>
                                      <option value="bounce">🎯 Bounce</option>
                                      <option value="pulse">💓 Pulse</option>
                                      <option value="shake">📳 Shake</option>
                                      <option value="swing">🔄 Swing</option>
                                      <option value="tada">🎉 Tada</option>
                                      <option value="wobble">〰️ Wobble</option>
                                      <option value="heartbeat">💗 Heartbeat</option>
                                      <option value="flash">⚡ Flash</option>
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">Animation plays on hover</p>
                                  </div>
                                </div>
                              )}

                              {/* FAQ Section */}
                              {section.type === 'faq' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="FAQ Section Title"
                                  />
                                  {(section.items || []).map((item, itemIndex) => (
                                    <div key={itemIndex} className="flex gap-2 items-start">
                                      <div className="flex-1 space-y-2">
                                        <input
                                          type="text"
                                          value={item.question || ''}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            const newItems = [...(section.items || [])];
                                            newItems[itemIndex] = { ...item, question: e.target.value };
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                          placeholder="Question"
                                        />
                                        <textarea
                                          value={item.answer || ''}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            const newItems = [...(section.items || [])];
                                            newItems[itemIndex] = { ...item, answer: e.target.value };
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          rows="2"
                                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                          placeholder="Answer"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSections = [...editData.sections];
                                          const newItems = section.items.filter((_, i) => i !== itemIndex);
                                          newSections[sectionIndex] = { ...section, items: newItems };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="text-red-500 hover:text-red-700 text-xs mt-2"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newSections = [...editData.sections];
                                      const newItems = [...(section.items || []), { question: '', answer: '' }];
                                      newSections[sectionIndex] = { ...section, items: newItems };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="text-blue-600 text-sm hover:underline"
                                  >
                                    + Add FAQ Item
                                  </button>
                                </div>
                              )}

                              {/* Features Section */}
                              {section.type === 'features' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Features Section Title"
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600">Background Color</label>
                                      <input
                                        type="color"
                                        value={section.bg_color || '#f8fafc'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600">Columns</label>
                                      <select
                                        value={section.columns || 3}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, columns: parseInt(e.target.value) };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      >
                                        <option value="2">2 Columns</option>
                                        <option value="3">3 Columns</option>
                                        <option value="4">4 Columns</option>
                                      </select>
                                    </div>
                                  </div>
                                  {(section.items || []).map((item, itemIndex) => (
                                    <div key={itemIndex} className="bg-slate-50 p-3 rounded border border-slate-200">
                                      <div className="flex justify-between mb-2">
                                        <span className="text-xs font-medium text-slate-600">Feature {itemIndex + 1}</span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newSections = [...editData.sections];
                                            const newItems = section.items.filter((_, i) => i !== itemIndex);
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="text-red-500 hover:text-red-700 text-xs"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          value={item.icon || ''}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            const newItems = [...(section.items || [])];
                                            newItems[itemIndex] = { ...item, icon: e.target.value };
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                          placeholder="Icon (emoji like 🚀 or ⚡)"
                                        />
                                        <input
                                          type="text"
                                          value={item.title || ''}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            const newItems = [...(section.items || [])];
                                            newItems[itemIndex] = { ...item, title: e.target.value };
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                          placeholder="Feature Title"
                                        />
                                        <textarea
                                          value={item.description || ''}
                                          onChange={(e) => {
                                            const newSections = [...editData.sections];
                                            const newItems = [...(section.items || [])];
                                            newItems[itemIndex] = { ...item, description: e.target.value };
                                            newSections[sectionIndex] = { ...section, items: newItems };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          rows="2"
                                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                          placeholder="Feature Description"
                                        />
                                      </div>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newSections = [...editData.sections];
                                      const newItems = [...(section.items || []), { icon: '✨', title: '', description: '' }];
                                      newSections[sectionIndex] = { ...section, items: newItems };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="text-blue-600 text-sm hover:underline"
                                  >
                                    + Add Feature
                                  </button>
                                </div>
                              )}

                              {/* Stats Section */}
                              {section.type === 'stats' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Stats Section Title (optional)"
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600">Background Color</label>
                                      <input
                                        type="color"
                                        value={section.bg_color || '#1e40af'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600">Text Color</label>
                                      <input
                                        type="color"
                                        value={section.text_color || '#ffffff'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, text_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                  </div>
                                  {(section.items || []).map((item, itemIndex) => (
                                    <div key={itemIndex} className="flex gap-2 items-center bg-slate-50 p-2 rounded">
                                      <input
                                        type="text"
                                        value={item.number || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          const newItems = [...(section.items || [])];
                                          newItems[itemIndex] = { ...item, number: e.target.value };
                                          newSections[sectionIndex] = { ...section, items: newItems };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-1/3 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="500+"
                                      />
                                      <input
                                        type="text"
                                        value={item.label || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          const newItems = [...(section.items || [])];
                                          newItems[itemIndex] = { ...item, label: e.target.value };
                                          newSections[sectionIndex] = { ...section, items: newItems };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="Students Trained"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newSections = [...editData.sections];
                                          const newItems = section.items.filter((_, i) => i !== itemIndex);
                                          newSections[sectionIndex] = { ...section, items: newItems };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="text-red-500 hover:text-red-700 text-xs"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newSections = [...editData.sections];
                                      const newItems = [...(section.items || []), { number: '', label: '' }];
                                      newSections[sectionIndex] = { ...section, items: newItems };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="text-blue-600 text-sm hover:underline"
                                  >
                                    + Add Stat
                                  </button>
                                </div>
                              )}

                              {/* Video Section */}
                              {section.type === 'video' && (
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, title: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Video Section Title (optional)"
                                  />
                                  <div>
                                    <label className="text-xs font-semibold text-slate-600 mb-2 block">Video Type</label>
                                    <div className="flex gap-2 mb-2">
                                      <label className="flex items-center text-sm">
                                        <input
                                          type="radio"
                                          name={`video-type-${sectionIndex}`}
                                          checked={section.video_type !== 'file'}
                                          onChange={() => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, video_type: 'youtube' };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="mr-1"
                                        />
                                        YouTube URL
                                      </label>
                                      <label className="flex items-center text-sm">
                                        <input
                                          type="radio"
                                          name={`video-type-${sectionIndex}`}
                                          checked={section.video_type === 'file'}
                                          onChange={() => {
                                            const newSections = [...editData.sections];
                                            newSections[sectionIndex] = { ...section, video_type: 'file' };
                                            setEditData({ ...editData, sections: newSections });
                                          }}
                                          className="mr-1"
                                        />
                                        Video File URL
                                      </label>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                                      {section.video_type === 'file' ? 'Video File URL' : 'YouTube Video URL'} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={section.video_url || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, video_url: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder={section.video_type === 'file' ? 'https://example.com/video.mp4' : 'https://www.youtube.com/watch?v=...'}
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                      {section.video_type === 'file' 
                                        ? '💡 Direct link to MP4, WebM, or OGG file' 
                                        : '💡 Paste the full YouTube URL (e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ)'}
                                    </p>
                                  </div>
                                  <textarea
                                    value={section.description || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, description: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    rows="2"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Video description (optional)"
                                  />
                                </div>
                              )}

                              {/* Testimonial Section */}
                              {section.type === 'testimonial' && (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-2">
                                    <input
                                      type="text"
                                      value={section.name || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, name: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Customer Name"
                                    />
                                    <input
                                      type="text"
                                      value={section.role || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, role: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Role/Position"
                                    />
                                  </div>
                                  <input
                                    type="text"
                                    value={section.company || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, company: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Company Name"
                                  />
                                  <textarea
                                    value={section.message || ''}
                                    onChange={(e) => {
                                      const newSections = [...editData.sections];
                                      newSections[sectionIndex] = { ...section, message: e.target.value };
                                      setEditData({ ...editData, sections: newSections });
                                    }}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                    placeholder="Testimonial message..."
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600">Profile Image URL</label>
                                      <input
                                        type="text"
                                        value={section.image_url || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, image_url: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="https://..."
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600">Rating (1-5)</label>
                                      <input
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={section.rating || 5}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, rating: parseInt(e.target.value) || 5 };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600">Background Color</label>
                                    <input
                                      type="color"
                                      value={section.bg_color || '#fef3c7'}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full h-10 rounded border border-slate-300"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Contact Form Section */}
                              {section.type === 'contact_form' && (
                                <div className="space-y-3">
                                  <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                                    <p className="text-sm text-blue-800">
                                      <strong>💡 How it works:</strong> A "Contact Us" button will appear on the page. When clicked, a beautiful modal popup with contact form will open.
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <input
                                      type="text"
                                      value={section.title || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, title: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Section Title (e.g., 'Ready to Start?')"
                                    />
                                    <input
                                      type="text"
                                      value={section.subtitle || ''}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, subtitle: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                      placeholder="Section Subtitle (optional)"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Button Text</label>
                                      <input
                                        type="text"
                                        value={section.button_text || 'Contact Us'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, button_text: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="Contact Us"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-slate-600 block mb-1">Button Color</label>
                                      <input
                                        type="color"
                                        value={section.button_color || '#3b82f6'}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, button_color: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full h-10 rounded border border-slate-300"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 block mb-1">Section Background Color</label>
                                    <input
                                      type="color"
                                      value={section.bg_color || '#ffffff'}
                                      onChange={(e) => {
                                        const newSections = [...editData.sections];
                                        newSections[sectionIndex] = { ...section, bg_color: e.target.value };
                                        setEditData({ ...editData, sections: newSections });
                                      }}
                                      className="w-full h-10 rounded border border-slate-300"
                                    />
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded border border-slate-200">
                                    <label className="text-xs font-semibold text-slate-700 block mb-2">Modal Popup Settings</label>
                                    <div className="space-y-2">
                                      <input
                                        type="text"
                                        value={section.modal_title || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, modal_title: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="Modal Title (e.g., 'Get In Touch')"
                                      />
                                      <input
                                        type="text"
                                        value={section.modal_subtitle || ''}
                                        onChange={(e) => {
                                          const newSections = [...editData.sections];
                                          newSections[sectionIndex] = { ...section, modal_subtitle: e.target.value };
                                          setEditData({ ...editData, sections: newSections });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                                        placeholder="Modal Subtitle (e.g., 'We'll respond within 24 hours')"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {(!editData.sections || editData.sections.length === 0) && (
                            <p className="text-slate-400 text-sm text-center py-4">
                              No content sections yet. Click the buttons above to add content.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={async () => {
                          try {
                            if (!editData.title || !editData.slug || !editData.hero_title) {
                              toast.error('Please fill in all required fields');
                              return;
                            }
                            if (editData.id) {
                              await axios.put(`${API}/admin/dynamic-pages/${editData.id}`, editData, getAuthHeaders());
                              toast.success('Page updated successfully');
                            } else {
                              await axios.post(`${API}/admin/dynamic-pages`, editData, getAuthHeaders());
                              toast.success('Page created successfully');
                            }
                            setIsEditing(false);
                            setEditData(null);
                            fetchAllData();
                          } catch (error) {
                            console.error('Error saving page:', error);
                            toast.error(error.response?.data?.detail || 'Failed to save page');
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} className="inline mr-2" />
                        Save Page
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(null);
                        }}
                        className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} className="inline mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Pages List */}
                <div className="space-y-4">
                  {dynamicPages.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Layout size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No custom pages yet. Click "Create New Page" to add one.</p>
                      <p className="text-sm mt-2">Custom pages will appear in the navigation menu automatically.</p>
                    </div>
                  ) : (
                    dynamicPages.map((page) => (
                      <div key={page.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-bold text-lg text-slate-900">{page.title}</h3>
                              {page.show_in_nav && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">In Nav</span>
                              )}
                              {!page.active && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Inactive</span>
                              )}
                            </div>
                            <p className="text-slate-500 text-sm mb-2">/page/{page.slug}</p>
                            <p className="text-slate-600 text-sm">{page.hero_subtitle || page.hero_title}</p>
                          </div>
                          <div className="flex space-x-2">
                            <a
                              href={`/page/${page.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-slate-600 hover:bg-slate-50 rounded-sm"
                              title="View Page"
                            >
                              <ExternalLink size={18} />
                            </a>
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                setEditData({ ...page });
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteDynamicPage(page.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-sm"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Page Content Tab */}
            {activeTab === 'pages' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Page Content Management
                </h2>
                
                {/* Page Selector */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {['about', 'services', 'courses', 'why-choose-us', 'contact'].map((page) => (
                    <button
                      key={page}
                      onClick={() => {
                        setSelectedPage(page);
                        fetchPageContent(page);
                      }}
                      className={`px-4 py-2 rounded-sm font-medium ${
                        selectedPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {page.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </button>
                  ))}
                </div>

                {/* Page Content Editor */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold mb-4 capitalize">{selectedPage.replace('-', ' ')} Page</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Title</label>
                      <input
                        type="text"
                        value={pageContent[selectedPage]?.hero_title || ''}
                        onChange={(e) => setPageContent(prev => ({
                          ...prev,
                          [selectedPage]: { ...prev[selectedPage], hero_title: e.target.value }
                        }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Page hero title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Hero Subtitle</label>
                      <textarea
                        value={pageContent[selectedPage]?.hero_subtitle || ''}
                        onChange={(e) => setPageContent(prev => ({
                          ...prev,
                          [selectedPage]: { ...prev[selectedPage], hero_subtitle: e.target.value }
                        }))}
                        rows="2"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Page hero subtitle/description"
                      />
                    </div>
                    <button
                      onClick={() => handleSavePageContent(selectedPage, pageContent[selectedPage])}
                      className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                    >
                      <Save size={18} className="inline mr-2" />
                      Save {selectedPage.replace('-', ' ')} Content
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Social Links Tab */}
            {activeTab === 'social' && companyInfo && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Social Media Links
                  </h2>
                  {!isEditing ? (
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditData({ 
                          social_links: companyInfo.social_links || {
                            facebook: '',
                            twitter: '',
                            linkedin: '',
                            instagram: '',
                            youtube: '',
                            whatsapp: ''
                          }
                        });
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                    >
                      <Edit2 size={18} />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/company-info`, { social_links: editData.social_links }, getAuthHeaders());
                            toast.success('Social links updated successfully');
                            setIsEditing(false);
                            fetchAllData();
                          } catch (error) {
                            toast.error('Failed to update social links');
                          }
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(null);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Facebook URL</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.facebook || '') : (companyInfo.social_links?.facebook || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, facebook: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Instagram URL</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.instagram || '') : (companyInfo.social_links?.instagram || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, instagram: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="https://instagram.com/yourpage"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Twitter/X URL</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.twitter || '') : (companyInfo.social_links?.twitter || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, twitter: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="https://twitter.com/yourpage"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">LinkedIn URL</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.linkedin || '') : (companyInfo.social_links?.linkedin || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, linkedin: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="https://linkedin.com/company/yourpage"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">YouTube URL</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.youtube || '') : (companyInfo.social_links?.youtube || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, youtube: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="https://youtube.com/@yourchannel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">WhatsApp Number</label>
                    <input
                      type="text"
                      value={isEditing ? (editData.social_links?.whatsapp || '') : (companyInfo.social_links?.whatsapp || '')}
                      onChange={(e) => setEditData({ ...editData, social_links: { ...editData.social_links, whatsapp: e.target.value } })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && stats && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Website Statistics
                  </h2>
                  {!isEditing ? (
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditData({ ...stats });
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                    >
                      <Edit2 size={18} />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateStats}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex items-center space-x-2 px-4 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {(isEditing ? editData.items : stats.items).map((item, index) => (
                    <div key={index} className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Label</label>
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e) => {
                            const newItems = [...editData.items];
                            newItems[index].label = e.target.value;
                            setEditData({ ...editData, items: newItems });
                          }}
                          disabled={!isEditing}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Value</label>
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) => {
                            const newItems = [...editData.items];
                            newItems[index].value = e.target.value;
                            setEditData({ ...editData, items: newItems });
                          }}
                          disabled={!isEditing}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Courses Management
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        title: '',
                        category: 'web_development',
                        description: '',
                        duration: '',
                        price: '',
                        discount_price: '',
                        level: 'Beginner',
                        features: [],
                        syllabus: [],
                        instructor: '',
                        active: true,
                        featured: false,
                        order: courses.length
                      });
                    }}
                    data-testid="add-course-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Add New Course</span>
                  </button>
                </div>

                {/* Add/Edit Course Form */}
                {isEditing && editData?.category && editData?.level && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editData.id ? 'Edit Course' : 'Add New Course'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Course Title *</label>
                        <input
                          type="text"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Full Stack Web Development"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Category *</label>
                        <select
                          value={editData.category}
                          onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="web_development">Web Development</option>
                          <option value="cloud">Cloud Computing</option>
                          <option value="data_science">Data Science</option>
                          <option value="cybersecurity">Cybersecurity</option>
                          <option value="networking">Networking</option>
                          <option value="mobile">Mobile Development</option>
                          <option value="devops">DevOps</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
                        <textarea
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows="3"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Brief course description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Duration *</label>
                        <input
                          type="text"
                          value={editData.duration}
                          onChange={(e) => setEditData({ ...editData, duration: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 3 months, 6 weeks"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Level</label>
                        <select
                          value={editData.level}
                          onChange={(e) => setEditData({ ...editData, level: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Price (₹)</label>
                        <input
                          type="number"
                          value={editData.price}
                          onChange={(e) => setEditData({ ...editData, price: e.target.value ? parseFloat(e.target.value) : '' })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 25000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Discount Price (₹)</label>
                        <input
                          type="number"
                          value={editData.discount_price}
                          onChange={(e) => setEditData({ ...editData, discount_price: e.target.value ? parseFloat(e.target.value) : '' })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 19999"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Instructor</label>
                        <input
                          type="text"
                          value={editData.instructor || ''}
                          onChange={(e) => setEditData({ ...editData, instructor: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Instructor name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Image URL</label>
                        <input
                          type="text"
                          value={editData.image_url || ''}
                          onChange={(e) => setEditData({ ...editData, image_url: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://..."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Features (comma-separated)</label>
                        <input
                          type="text"
                          value={editData.features?.join(', ') || ''}
                          onChange={(e) => setEditData({ ...editData, features: e.target.value.split(',').map(f => f.trim()).filter(f => f) })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Live Projects, Job Assistance, Certification"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Syllabus Topics (comma-separated)</label>
                        <input
                          type="text"
                          value={editData.syllabus?.join(', ') || ''}
                          onChange={(e) => setEditData({ ...editData, syllabus: e.target.value.split(',').map(s => s.trim()).filter(s => s) })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="HTML/CSS, JavaScript, React, Node.js"
                        />
                      </div>
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.active}
                            onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Active</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.featured}
                            onChange={(e) => setEditData({ ...editData, featured: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Featured</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={async () => {
                          try {
                            const courseData = {
                              ...editData,
                              price: editData.price || null,
                              discount_price: editData.discount_price || null,
                            };
                            if (editData.id) {
                              await axios.put(`${API}/admin/courses/${editData.id}`, courseData, getAuthHeaders());
                              toast.success('Course updated successfully');
                            } else {
                              await axios.post(`${API}/admin/courses`, courseData, getAuthHeaders());
                              toast.success('Course created successfully');
                            }
                            setIsEditing(false);
                            setEditData(null);
                            fetchAllData();
                          } catch (error) {
                            console.error('Error saving course:', error);
                            toast.error('Failed to save course');
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} className="inline mr-2" />
                        Save Course
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(null);
                        }}
                        className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} className="inline mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Courses List */}
                <div className="space-y-4">
                  {courses.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No courses added yet. Click "Add New Course" to create one.</p>
                    </div>
                  ) : (
                    courses.map((course) => (
                      <div key={course.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                                {course.category.replace('_', ' ')}
                              </span>
                              <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                                course.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                                course.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {course.level}
                              </span>
                              {course.featured && (
                                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm rounded-full font-medium flex items-center">
                                  <Star size={12} className="mr-1" /> Featured
                                </span>
                              )}
                              {!course.active && (
                                <span className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full font-medium">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <h3 className="font-bold text-lg text-slate-900">{course.title}</h3>
                            <p className="text-slate-600 mt-1 text-sm">{course.description}</p>
                            <div className="mt-2 flex items-center space-x-4 text-sm text-slate-500">
                              <span>Duration: {course.duration}</span>
                              {course.price && (
                                <span>
                                  Price: ₹{course.discount_price || course.price}
                                  {course.discount_price && course.discount_price < course.price && (
                                    <span className="line-through ml-1 text-slate-400">₹{course.price}</span>
                                  )}
                                </span>
                              )}
                            </div>
                            {course.features && course.features.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {course.features.map((feature, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                setEditData({ ...course });
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteCourse(course.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Certificates Tab */}
            {activeTab === 'certificates' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Certificates & Registrations
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        title: '',
                        description: '',
                        registration_number: '',
                        issuing_authority: '',
                        image_url: '',
                        valid_from: '',
                        valid_until: '',
                        active: true,
                        order: certificates.length
                      });
                    }}
                    data-testid="add-certificate-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Add Certificate</span>
                  </button>
                </div>

                {/* Add/Edit Certificate Form */}
                {isEditing && editData?.hasOwnProperty('registration_number') && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editData.id ? 'Edit Certificate' : 'Add New Certificate'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Certificate Title *</label>
                        <input
                          type="text"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., ISO 9001:2015, MSME Registration"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Registration Number</label>
                        <input
                          type="text"
                          value={editData.registration_number || ''}
                          onChange={(e) => setEditData({ ...editData, registration_number: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., UDYAM-XX-XX-XXXXXXX"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                          value={editData.description || ''}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows="2"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Brief description of the certificate"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Issuing Authority</label>
                        <input
                          type="text"
                          value={editData.issuing_authority || ''}
                          onChange={(e) => setEditData({ ...editData, issuing_authority: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Ministry of MSME, ISO"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Certificate Logo URL</label>
                        <input
                          type="text"
                          value={editData.image_url || ''}
                          onChange={(e) => setEditData({ ...editData, image_url: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Valid From</label>
                        <input
                          type="text"
                          value={editData.valid_from || ''}
                          onChange={(e) => setEditData({ ...editData, valid_from: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., January 2024"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Valid Until (if applicable)</label>
                        <input
                          type="text"
                          value={editData.valid_until || ''}
                          onChange={(e) => setEditData({ ...editData, valid_until: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., December 2026 or leave blank"
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editData.active}
                            onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700">Active (Show on website)</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-6">
                      <button
                        onClick={async () => {
                          try {
                            if (editData.id) {
                              await axios.put(`${API}/admin/certificates/${editData.id}`, editData, getAuthHeaders());
                              toast.success('Certificate updated successfully');
                            } else {
                              await axios.post(`${API}/admin/certificates`, editData, getAuthHeaders());
                              toast.success('Certificate created successfully');
                            }
                            setIsEditing(false);
                            setEditData(null);
                            fetchAllData();
                          } catch (error) {
                            console.error('Error saving certificate:', error);
                            toast.error('Failed to save certificate');
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                      >
                        <Save size={18} className="inline mr-2" />
                        Save Certificate
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(null);
                        }}
                        className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                      >
                        <X size={18} className="inline mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Certificates List */}
                <div className="space-y-4">
                  {certificates.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Award size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No certificates added yet. Click "Add Certificate" to create one.</p>
                    </div>
                  ) : (
                    certificates.map((cert) => (
                      <div key={cert.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-4">
                            {cert.image_url ? (
                              <img src={cert.image_url} alt={cert.title} className="w-16 h-16 object-contain" />
                            ) : (
                              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Award size={32} className="text-blue-600" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="font-bold text-lg text-slate-900">{cert.title}</h3>
                                {!cert.active && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Inactive</span>
                                )}
                              </div>
                              {cert.description && (
                                <p className="text-slate-600 text-sm mb-1">{cert.description}</p>
                              )}
                              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                {cert.registration_number && (
                                  <span>Reg. No: <strong>{cert.registration_number}</strong></span>
                                )}
                                {cert.issuing_authority && (
                                  <span>By: {cert.issuing_authority}</span>
                                )}
                                {cert.valid_from && (
                                  <span>From: {cert.valid_from}</span>
                                )}
                                {cert.valid_until && (
                                  <span>Until: {cert.valid_until}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                setEditData({ ...cert });
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteCertificate(cert.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Services Management
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        category: 'training',
                        title: '',
                        description: '',
                        icon: '',
                        features: [],
                        active: true,
                        order: services.length
                      });
                    }}
                    data-testid="add-service-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Add New Service</span>
                  </button>
                </div>

                {/* Add/Edit Service Form */}
                {isEditing && editData.category && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editData.id ? 'Edit Service' : 'Add New Service'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Category</label>
                        <select
                          value={editData.category}
                          onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="training">Training</option>
                          <option value="placement">Placement</option>
                          <option value="it_solution">IT Solution</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Title</label>
                        <input
                          type="text"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Service title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows="3"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Service description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Features (comma-separated)</label>
                        <input
                          type="text"
                          value={editData.features?.join(', ') || ''}
                          onChange={(e) => setEditData({ ...editData, features: e.target.value.split(',').map(f => f.trim()) })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Feature 1, Feature 2, Feature 3"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editData.active}
                          onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <label className="text-sm font-semibold text-slate-700">Active</label>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              if (editData.id) {
                                // Update existing
                                await axios.put(`${API}/admin/services/${editData.id}`, {
                                  category: editData.category,
                                  title: editData.title,
                                  description: editData.description,
                                  icon: editData.icon,
                                  features: editData.features,
                                  active: editData.active,
                                  order: editData.order
                                }, getAuthHeaders());
                                toast.success('Service updated successfully');
                              } else {
                                // Create new
                                await axios.post(`${API}/admin/services`, editData, getAuthHeaders());
                                toast.success('Service created successfully');
                              }
                              setIsEditing(false);
                              setEditData(null);
                              fetchAllData();
                            } catch (error) {
                              console.error('Error saving service:', error);
                              toast.error('Failed to save service');
                            }
                          }}
                          className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                        >
                          <Save size={18} className="inline mr-2" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditData(null);
                          }}
                          className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                        >
                          <X size={18} className="inline mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                              {service.category}
                            </span>
                            {!service.active && (
                              <span className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full font-medium">
                                Inactive
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-lg text-slate-900">{service.title}</h3>
                          <p className="text-slate-600 mt-2">{service.description}</p>
                          {service.features && service.features.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {service.features.map((feature, idx) => (
                                <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setIsEditing(true);
                              setEditData({ ...service });
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteService(service.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Testimonials Tab */}
            {activeTab === 'testimonials' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Testimonials
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        name: '',
                        role: '',
                        company: '',
                        message: '',
                        rating: 5
                      });
                    }}
                    data-testid="add-testimonial-button"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Add Testimonial</span>
                  </button>
                </div>

                {/* Add Testimonial Form */}
                {isEditing && editData.name !== undefined && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">Add New Testimonial</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Client name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Role</label>
                        <input
                          type="text"
                          value={editData.role}
                          onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Job title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Company (Optional)</label>
                        <input
                          type="text"
                          value={editData.company || ''}
                          onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Company name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
                        <textarea
                          value={editData.message}
                          onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                          rows="4"
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Testimonial message"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Rating</label>
                        <select
                          value={editData.rating}
                          onChange={(e) => setEditData({ ...editData, rating: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5 Stars</option>
                          <option value="4">4 Stars</option>
                          <option value="3">3 Stars</option>
                          <option value="2">2 Stars</option>
                          <option value="1">1 Star</option>
                        </select>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/testimonials`, editData, getAuthHeaders());
                              toast.success('Testimonial added successfully');
                              setIsEditing(false);
                              setEditData(null);
                              fetchAllData();
                            } catch (error) {
                              console.error('Error adding testimonial:', error);
                              toast.error('Failed to add testimonial');
                            }
                          }}
                          className="px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
                        >
                          <Save size={18} className="inline mr-2" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditData(null);
                          }}
                          className="px-6 py-2 bg-slate-600 text-white rounded-sm hover:bg-slate-700"
                        >
                          <X size={18} className="inline mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-slate-900">{testimonial.name}</h3>
                          <p className="text-sm text-slate-500">{testimonial.role} {testimonial.company && `at ${testimonial.company}`}</p>
                          <p className="text-slate-700 mt-3 italic">"{testimonial.message}"</p>
                          <div className="flex mt-2">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <span key={i} className="text-yellow-400">★</span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteTestimonial(testimonial.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Enquiries Tab */}
            {activeTab === 'enquiries' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Enquiries
                </h2>
                <div className="space-y-4">
                  {enquiries.map((enquiry) => (
                    <div key={enquiry.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-bold text-lg text-slate-900">{enquiry.name}</h3>
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                              {enquiry.service}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600 space-y-1 mb-3">
                            <p>Email: {enquiry.email}</p>
                            <p>Phone: {enquiry.phone}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(enquiry.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-slate-700">{enquiry.message}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteEnquiry(enquiry.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                {/* Change Password Section */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Settings size={24} className="text-blue-600" />
                    Change Password
                  </h2>
                  <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        minLength="6"
                      />
                      <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        minLength="6"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Change Password
                    </button>
                  </form>
                </div>

                {/* User Management Section */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Users size={24} className="text-blue-600" />
                    Admin Users
                  </h2>

                  {/* Create New User Form */}
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-4">Create New Admin User</h3>
                    <form onSubmit={handleCreateUser} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          value={newUserPassword}
                          onChange={(e) => setNewUserPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                          minLength="6"
                        />
                      </div>
                      <button
                        type="submit"
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        Create User
                      </button>
                    </form>
                  </div>

                  {/* Users List */}
                  <div className="space-y-3">
                    <h3 className="font-bold text-lg mb-3">Existing Users</h3>
                    {users && users.length > 0 ? (
                      users.map((user) => (
                        <div key={user.username} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users size={20} className="text-blue-600" />
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">{user.username}</p>
                              {user.created_at && (
                                <p className="text-xs text-slate-500">
                                  Created: {new Date(user.created_at).toLocaleDateString()}
                                </p>
                              )}
                              {user.username === 'admin' && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                  Default User
                                </span>
                              )}
                            </div>
                          </div>
                          {user.username !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(user.username)}
                              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4">No users found</p>
                    )}
                  </div>
                </div>

                {/* Security Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <div className="text-yellow-600 mt-1">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-yellow-800 mb-1">Security Notice</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• Change the default admin password immediately</li>
                        <li>• Use strong passwords (minimum 6 characters, recommend 12+)</li>
                        <li>• Cannot delete the default 'admin' user</li>
                        <li>• Cannot delete your own account while logged in</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}